import React, { useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Calendar, User, ArrowRight, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { blogData } from './dummyData';

export const BlogsSection = () => {
  const blogsRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('slide-up');
            }, index * 150);
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = blogsRef.current?.querySelectorAll('.blog-card');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  const latestBlogs = blogData.slice(0, 3);

  const handleReadMore = (externalLink) => {
    window.open(externalLink, '_blank');
  };

  const handleSeeMoreBlogs = () => {
    navigate('/blogs');
  };

  return (
    <section id="blogs" className="py-24 bg-[hsl(var(--muted))]" ref={blogsRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-[hsl(var(--primary))]/10 px-4 py-2 rounded-full">
            <BookOpen className="w-5 h-5 text-[hsl(var(--primary))]" />
            <span className="text-sm font-semibold text-[hsl(var(--primary))]">
              Health Insights
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))]">
            Latest Medical
            <span className="block mt-2 text-gradient">Blogs & Articles</span>
          </h2>
          <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
            Stay informed with the latest healthcare insights, medical advancements, and wellness tips from our expert doctors.
          </p>
        </div>

        {/* Blogs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {latestBlogs.map((blog, index) => (
            <div
              key={blog.id}
              className="blog-card opacity-0"
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <Card className="group h-full hover:shadow-[var(--shadow-xl)] transition-all duration-500 hover:-translate-y-2 border-2 border-transparent hover:border-[hsl(var(--primary))]/20">
                <CardContent className="p-6 space-y-4 h-full flex flex-col">
                  {/* Meta Information */}
                  <div className="flex items-center justify-between text-sm text-[hsl(var(--muted-foreground))]">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4" />
                      <span>{blog.author}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(blog.date).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Blog Title */}
                  <CardTitle className="text-xl font-bold text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--primary))] transition-colors line-clamp-2">
                    {blog.title}
                  </CardTitle>

                  {/* Blog Excerpt */}
                  <p className="text-[hsl(var(--muted-foreground))] leading-relaxed line-clamp-3 flex-grow">
                    {blog.excerpt}
                  </p>

                  {/* Read Time and Button */}
                  <div className="flex items-center justify-between pt-4 border-t border-[hsl(var(--border))]">
                    <span className="text-sm text-[hsl(var(--primary))] font-medium">
                      {blog.readTime}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReadMore(blog.externalLink)}
                      className="group font-semibold"
                    >
                      Read More
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {/* See More Button */}
        <div className="text-center">
          <Button
            variant="premium"
            size="lg"
            onClick={handleSeeMoreBlogs}
            className="group font-semibold"
          >
            <BookOpen className="w-5 h-5 mr-2" />
            See More Blogs
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};